<template>
  <div class="checkin" :class="{ kiosk: isKiosk }">
    <!-- 简易入口保护：防止外链被随便刷 -->
    <div v-if="!unlocked" class="gate card">
      <h2>{{ pageTitle }}</h2>
      <p class="gate-desc">请输入俱乐部编码与签到口令后使用（教练/前台设备）</p>
      <el-input
        v-model="clubCode"
        placeholder="俱乐部编码（如 yuedong）"
        size="medium"
        style="margin-bottom:12px"
      />
      <el-input
        v-model="pinInput"
        type="password"
        placeholder="签到口令"
        size="medium"
        show-password
        @keyup.enter.native="unlock"
      />
      <el-button type="primary" class="gate-btn" @click="unlock">进入签到</el-button>
      <p class="gate-hint">管理员已登录可直接进入；默认口令见管理后台说明</p>
    </div>

    <template v-else>
      <div class="hero">
        <h2>{{ pageTitle }}</h2>
        <p>{{ pageDesc }}</p>
      </div>

      <div class="consent card">
        拍照仅用于本机构课时签到身份比对，完成后立即删除现场照片，不保存到本机。请确保光线充足、面部清晰。
        <router-link to="/privacy">隐私说明</router-link>
      </div>

      <div class="camera-area card">
        <video
          ref="video"
          v-show="cameraActive && !previewImage"
          autoplay
          playsinline
          muted
          class="video-preview"
        />
        <img v-if="previewImage" :src="previewImage" class="preview-img" alt="预览" />
        <div v-if="!previewImage && !cameraActive" class="camera-placeholder">
          <i class="el-icon-camera" />
          <p>{{ statusText }}</p>
        </div>
        <div v-if="cameraActive && !previewImage" class="face-guide" />
        <div v-if="recognizing" class="overlay">
          <i class="el-icon-loading" /> 正在识别…
        </div>
      </div>

      <div v-if="result" class="result-card card" :class="resultClass">
        <div class="result-title">{{ resultTitle }}</div>
        <div v-if="result.studentName" class="result-row">学员：{{ result.studentName }}</div>
        <div v-if="result.similarityScore != null" class="result-row">
          相似度：{{ formatScore(result.similarityScore) }}
        </div>
        <div v-if="result.remainingHours != null" class="result-row">
          {{ hoursLabel }}：{{ result.remainingHours }}
        </div>
        <div v-if="result.owedHours > 0" class="result-row owed">欠课：{{ result.owedHours }} 课时</div>
        <div class="result-msg">{{ result.message }}</div>
        <div v-if="autoResetSeconds > 0" class="auto-tip">{{ autoResetSeconds }} 秒后可签下一位</div>
      </div>

      <div class="actions">
        <div v-if="!previewImage" class="pay-box">
          <div class="pay-label">本次扣课时</div>
          <el-radio-group v-model="hoursDeducted">
            <el-radio :label="0.5">0.5 节</el-radio>
            <el-radio :label="1">1 节</el-radio>
          </el-radio-group>
        </div>
        <div v-if="isPrivate && !previewImage" class="pay-box">
          <div class="pay-label">本次怎么扣</div>
          <el-radio-group v-model="payMode">
            <el-radio label="private">直接划小课</el-radio>
            <el-radio label="exchange">大课抵小课</el-radio>
          </el-radio-group>
          <p class="pay-hint">{{ payMode === 'exchange' ? '小课不够按该学员比例扣大课；未开通或大课不够会失败。' : '小课够就划，不够记欠，不会自动用大课抵。' }}</p>
        </div>
        <template v-if="!previewImage">
          <el-button
            v-if="!cameraActive"
            type="primary"
            class="action-btn primary"
            :loading="openingCamera"
            @click="openCamera"
          >
            打开摄像头
          </el-button>
          <el-button
            v-else
            type="primary"
            class="action-btn primary"
            :disabled="recognizing"
            @click="snapAndSubmit"
          >
            拍照并签到
          </el-button>
          <el-button class="action-btn" :disabled="recognizing" @click="chooseFile">
            相册 / 系统相机
          </el-button>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            capture="user"
            hidden
            @change="onFileChange"
          />
        </template>
        <template v-else>
          <el-button
            type="primary"
            class="action-btn primary"
            :loading="recognizing"
            :disabled="!!result && result.ok"
            @click="submitPreview"
          >
            {{ recognizing ? '识别中…' : '确认签到' }}
          </el-button>
          <el-button class="action-btn" :disabled="recognizing" @click="retake">重拍</el-button>
        </template>
      </div>

      <div class="footer-links">
        <a href="javascript:;" @click="lockAgain">锁定签到页</a>
        <span>·</span>
        <router-link to="/">返回首页</router-link>
        <span>·</span>
        <router-link to="/login">管理后台</router-link>
      </div>
    </template>
  </div>
</template>

<script>
import { faceCheckIn } from '../../api'
import { isAdminLoggedIn } from '../../utils/auth'

/** 现场签到口令（必须在管理后台设置，不再内置默认口令） */
const PIN_KEY = 'clockout_checkin_pin'
const UNLOCK_KEY = 'clockout_checkin_unlocked'

export default {
  name: 'CheckIn',
  data() {
    return {
      unlocked: false,
      clubCode: localStorage.getItem('club_code') || '',
      pinInput: '',
      openingCamera: false,
      cameraActive: false,
      stream: null,
      previewImage: '',
      recognizing: false,
      statusText: '点击「打开摄像头」开始',
      result: null,
      autoResetSeconds: 0,
      autoResetTimer: null,
      countdownTimer: null,
      courseType: 1,
      hoursDeducted: 1,
      payMode: 'private'
    }
  },
  computed: {
    isKiosk() {
      return !!(this.$route.meta && this.$route.meta.kiosk)
    },
    isPrivate() {
      return this.courseType === 2
    },
    pageTitle() {
      return this.isPrivate ? '小课刷脸签到' : '大课刷脸签到'
    },
    pageDesc() {
      return this.isPrivate
        ? '正对摄像头拍照。默认划 1 节小课，不够记欠；要用大课抵请先在下方改选。'
        : '正对摄像头拍照，识别后扣除 1 节大课，无需手动选学员。'
    },
    hoursLabel() {
      return this.isPrivate ? '小课剩余' : '大课剩余'
    },
    resultClass() {
      if (!this.result) return ''
      if (this.result.needPayment) return 'warn'
      return this.result.ok ? 'success' : 'fail'
    },
    resultTitle() {
      if (!this.result) return ''
      if (this.result.needPayment) return '签到成功（欠费）'
      return this.result.ok ? '签到成功' : '未能完成签到'
    }
  },
  created() {
    const q = this.$route.query || {}
    this.courseType = Number(q.courseType) === 2 ? 2 : 1
  },
  mounted() {
    const q = this.$route.query || {}
    if (q.clubCode) {
      const code = String(q.clubCode).trim()
      this.clubCode = code
      localStorage.setItem('club_code', code)
    }
    this.courseType = Number(q.courseType) === 2 ? 2 : 1
    document.title = this.pageTitle
    this.tryAutoUnlock()
  },
  beforeDestroy() {
    this.stopCamera()
    this.clearAutoReset()
  },
  methods: {
    tryAutoUnlock() {
      // 刷脸扣课时必须管理员登录；仅本地口令不足以调用接口
      if (isAdminLoggedIn() && (this.clubCode || localStorage.getItem('club_code'))) {
        const code = (this.clubCode || localStorage.getItem('club_code') || '').trim()
        if (!code) {
          this.$message.warning('请先绑定俱乐部编码')
          return
        }
        localStorage.setItem('club_code', code)
        this.clubCode = code
        this.unlocked = true
        return
      }
      if (sessionStorage.getItem(UNLOCK_KEY) === '1' && isAdminLoggedIn()) {
        this.unlocked = true
      }
    },
    unlock() {
      if (!isAdminLoggedIn()) {
        this.$message.error('请先在管理后台登录管理员账号，再打开签到页')
        return
      }
      const expected = localStorage.getItem(PIN_KEY)
      if (!expected) {
        this.$message.error('请先在管理后台「刷脸设置」中设置签到口令')
        return
      }
      if (String(this.pinInput).trim() === expected) {
        const code = (this.clubCode || localStorage.getItem('club_code') || '').trim()
        if (!code) {
          this.$message.warning('请填写俱乐部编码')
          return
        }
        localStorage.setItem('club_code', code)
        this.clubCode = code
        sessionStorage.setItem(UNLOCK_KEY, '1')
        this.unlocked = true
        this.pinInput = ''
        this.$message.success('已进入签到')
        return
      }
      this.$message.error('口令错误')
    },
    lockAgain() {
      sessionStorage.removeItem(UNLOCK_KEY)
      this.unlocked = false
      this.stopCamera()
      this.resetCapture()
    },
    async openCamera() {
      this.resetCapture(false)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$message.warning('当前浏览器不支持摄像头，请用「相册 / 系统相机」')
        this.chooseFile()
        return
      }
      this.openingCamera = true
      try {
        this.stopCamera()
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'user' },
            width: { ideal: 720 },
            height: { ideal: 720 }
          }
        })
        this.cameraActive = true
        this.statusText = '请将面部置于框内，点击「拍照并签到」'
        await this.$nextTick()
        if (this.$refs.video) {
          this.$refs.video.srcObject = this.stream
          try {
            await this.$refs.video.play()
          } catch (e) { /* ignore */ }
        }
      } catch (e) {
        console.warn('getUserMedia fail', e)
        this.$message.warning('无法打开摄像头，请检查权限后重试，或改用选人签到')
      } finally {
        this.openingCamera = false
      }
    },
    snapAndSubmit() {
      const video = this.$refs.video
      if (!video || !video.videoWidth) {
        this.$message.warning('摄像头尚未就绪，请稍候再拍')
        return
      }
      const canvas = document.createElement('canvas')
      const w = video.videoWidth
      const h = video.videoHeight
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(video, 0, 0, w, h)
      const base64 = canvas.toDataURL('image/jpeg', 0.88)
      this.previewImage = base64
      this.stopCamera()
      this.sendFace(base64)
    },
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop())
        this.stream = null
      }
      this.cameraActive = false
    },
    chooseFile() {
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.clearAutoReset()
      this.result = null
      const reader = new FileReader()
      reader.onload = () => {
        this.previewImage = reader.result
        this.stopCamera()
      }
      reader.readAsDataURL(file)
      e.target.value = ''
    },
    retake() {
      this.resetCapture()
      this.openCamera()
    },
    resetCapture(clearResult = true) {
      this.previewImage = ''
      if (clearResult) this.result = null
      this.clearAutoReset()
    },
    submitPreview() {
      if (this.previewImage) this.sendFace(this.previewImage)
    },
    formatScore(score) {
      const n = Number(score)
      if (isNaN(n)) return score
      // 后端可能是 0~1 或已经是百分数
      return (n <= 1 ? (n * 100).toFixed(1) : n.toFixed(1)) + '%'
    },
    async sendFace(base64) {
      this.recognizing = true
      this.clearAutoReset()
      try {
        if (!isAdminLoggedIn()) {
          this.$message.error('管理员登录已失效，请重新登录后再签到')
          this.unlocked = false
          return
        }
        const res = await faceCheckIn(
          base64,
          this.hoursDeducted || 1,
          this.clubCode || localStorage.getItem('club_code') || '',
          this.courseType,
          this.isPrivate && this.payMode === 'exchange'
        )
        const data = res.data || {}
        const needPayment = !!data.needPayment
        const msg = data.message || ''
        const duplicate = /请勿重复/.test(msg)
        // 成功扣课或欠费记录：都有 studentId；识别失败 / 重复：studentId 为空
        const identified = data.studentId != null
        this.result = {
          ok: identified && !needPayment,
          needPayment,
          studentName: data.studentName,
          message: msg || (identified ? '签到完成' : '未识别到已录入学员'),
          remainingHours: data.remainingHours,
          owedHours: data.owedHours,
          similarityScore: data.similarityScore
        }
        this.previewImage = ''
        if (needPayment) {
          this.$message.warning(msg || '课时不足，请缴费')
          this.scheduleAutoReset(4)
        } else if (identified) {
          this.$message.success(msg || '签到成功')
          this.scheduleAutoReset(3)
        } else {
          this.$message.error(duplicate ? msg : (msg || '未识别到已录入学员，请重拍'))
        }
      } catch (e) {
        const msg = (e && (e.bizMessage || e.message)) || '识别失败，请重试'
        const data = (e && e.bizData) || {}
        this.result = {
          ok: false,
          needPayment: !!data.needPayment,
          studentName: data.studentName,
          message: msg,
          remainingHours: data.remainingHours,
          owedHours: data.owedHours,
          similarityScore: data.similarityScore
        }
        this.previewImage = ''
        this.$message.error(msg)
        this.scheduleAutoReset(3)
      } finally {
        this.recognizing = false
      }
    },
    scheduleAutoReset(seconds) {
      this.clearAutoReset()
      this.autoResetSeconds = seconds
      this.countdownTimer = setInterval(() => {
        this.autoResetSeconds -= 1
        if (this.autoResetSeconds <= 0) clearInterval(this.countdownTimer)
      }, 1000)
      this.autoResetTimer = setTimeout(() => {
        this.resetCapture()
        this.statusText = '准备下一位，点击「打开摄像头」'
        this.clearAutoReset()
      }, seconds * 1000)
    },
    clearAutoReset() {
      if (this.autoResetTimer) clearTimeout(this.autoResetTimer)
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.autoResetTimer = null
      this.countdownTimer = null
      this.autoResetSeconds = 0
    }
  }
}
</script>

<style scoped>
.checkin {
  max-width: 480px;
  margin: 0 auto;
}
.checkin.kiosk {
  max-width: 560px;
  padding-bottom: 24px;
}
.hero h2 {
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px;
}
.hero p {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 16px;
}
.card {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 14px;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
}
.consent {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}
.consent a {
  color: #6366f1;
  margin-left: 4px;
}
.camera-area {
  position: relative;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0f172a;
  padding: 0;
}
.video-preview,
.preview-img {
  width: 100%;
  max-height: 420px;
  object-fit: cover;
  display: block;
}
.camera-placeholder {
  text-align: center;
  color: #94a3b8;
  padding: 48px 24px;
}
.camera-placeholder i {
  font-size: 52px;
  display: block;
  margin-bottom: 12px;
}
.face-guide {
  pointer-events: none;
  position: absolute;
  width: 58%;
  aspect-ratio: 3 / 4;
  max-width: 240px;
  border: 3px solid rgba(255, 255, 255, 0.75);
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.35);
}
.overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  gap: 8px;
}
.result-card.success {
  border-left: 4px solid #22c55e;
}
.result-card.warn {
  border-left: 4px solid #f59e0b;
}
.result-card.fail {
  border-left: 4px solid #ef4444;
}
.result-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}
.result-row {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 4px;
}
.result-row.owed {
  color: #ef4444;
}
.result-msg {
  font-size: 14px;
  color: #1e293b;
  margin-top: 8px;
}
.auto-tip {
  margin-top: 10px;
  font-size: 13px;
  color: #6366f1;
}
.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}
.pay-box {
  background: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
}
.pay-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}
.pay-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}
.action-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  border-radius: 999px;
}
.action-btn.primary {
  font-weight: 600;
}
.footer-links {
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
}
.footer-links a {
  color: #6366f1;
  text-decoration: none;
}
.gate h2 {
  margin: 0 0 8px;
  font-size: 20px;
}
.gate-desc {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 16px;
}
.gate-btn {
  width: 100%;
  margin-top: 12px;
  height: 44px;
}
.gate-hint {
  margin-top: 12px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}
</style>
